import { useLiveQuery } from 'dexie-react-hooks';
import { Pause, Play, X } from 'lucide-react';
import { db } from '@/db/schema';
import { useDownloads } from '@/features/downloads/DownloadProvider';
import { formatBytes, formatPercent } from '@/lib/format';
import type { DownloadJob } from '@/db/schema';

/** Fila de downloads com progresso item a item (F12-T10 a T13). */
export function DownloadQueue(): React.ReactElement | null {
  const { jobs, paused, pause, resume, cancel, cancelAll } = useDownloads();

  const names = useLiveQuery(async () => {
    if (!jobs || jobs.length === 0) return new Map<string, string>();
    const signs = await db.signs.bulkGet(jobs.map((job) => job.signId));
    return new Map(
      signs.filter((sign) => sign !== undefined).map((sign) => [sign.id, sign.name]),
    );
  }, [jobs?.map((job) => job.signId).join('|')]);

  const pending = (jobs ?? []).filter((job) => job.status !== 'done');
  if (pending.length === 0) return null;

  const totalBytes = pending.reduce((sum, job) => sum + (job.totalBytes ?? 0), 0);
  const doneBytes = pending.reduce((sum, job) => sum + job.bytesDownloaded, 0);

  return (
    <section className="rounded-xl bg-slate-800 p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-medium">
          Baixando
          <span className="ml-2 text-sm font-normal text-slate-400">
            {pending.length} {pending.length === 1 ? 'item' : 'itens'}
          </span>
        </h2>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={paused ? resume : pause}
            className="flex min-h-11 items-center gap-1.5 rounded-lg bg-slate-700 px-3 text-sm active:bg-slate-600"
          >
            {paused ? <Play size={16} aria-hidden /> : <Pause size={16} aria-hidden />}
            {paused ? 'Retomar' : 'Pausar'}
          </button>

          <button
            type="button"
            onClick={() => void cancelAll()}
            className="min-h-11 rounded-lg px-3 text-sm text-slate-400 active:bg-slate-700"
          >
            Cancelar tudo
          </button>
        </div>
      </div>

      {totalBytes > 0 && (
        <p className="mt-1 text-sm text-slate-400">
          {formatBytes(doneBytes)} de {formatBytes(totalBytes)}
        </p>
      )}

      <ul className="mt-3 flex flex-col gap-2">
        {pending.slice(0, 20).map((job) => (
          <JobRow
            key={job.id}
            job={job}
            name={names?.get(job.signId) ?? job.signId}
            onCancel={() => void cancel(job.signId)}
          />
        ))}
      </ul>

      {pending.length > 20 && (
        <p className="mt-2 text-xs text-slate-500">… e mais {pending.length - 20} na fila.</p>
      )}
    </section>
  );
}

const STATUS_LABEL: Record<DownloadJob['status'], string> = {
  queued: 'Na fila',
  downloading: 'Baixando',
  paused: 'Pausado',
  error: 'Erro',
  done: 'Concluído',
};

function JobRow({
  job,
  name,
  onCancel,
}: {
  job: DownloadJob;
  name: string;
  onCancel: () => void;
}): React.ReactElement {
  const ratio = job.totalBytes ? job.bytesDownloaded / job.totalBytes : undefined;

  return (
    <li className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm">{name}</span>
          <span className="shrink-0 text-xs tabular-nums text-slate-400">
            {job.status === 'downloading' && ratio !== undefined
              ? formatPercent(ratio)
              : STATUS_LABEL[job.status]}
          </span>
        </div>

        <div
          className="mt-1 h-1 overflow-hidden rounded-full bg-slate-700"
          role="progressbar"
          aria-label={`Progresso de ${name}`}
          aria-valuenow={ratio !== undefined ? Math.round(ratio * 100) : undefined}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full transition-[width] ${job.status === 'error' ? 'bg-red-400' : 'bg-sky-400'}`}
            style={{ width: ratio !== undefined ? `${ratio * 100}%` : '0%' }}
          />
        </div>

        {job.status === 'error' && job.lastError && (
          <p className="mt-1 text-xs text-red-300">{job.lastError}</p>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        aria-label={`Cancelar download de ${name}`}
        className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-slate-400 active:bg-slate-700"
      >
        <X size={16} aria-hidden />
      </button>
    </li>
  );
}
